import React from 'react';
import { Box, Card, Typography, ButtonBase } from '@mui/material';
import { Lookup } from '../../../../contexts/MetadataContext';
import { snakeCase } from 'lodash';

interface PropertyTypeCardProps {
  option: Lookup;
  isSelected: boolean;
  onClick: () => void;
}

const PropertyTypeCard: React.FC<PropertyTypeCardProps> = ({
  option,
  isSelected,
  onClick,
}) => {
  const getSvgSrc = (lookupKey: string) => {
    return `/property_type/${snakeCase(lookupKey)}.svg`;
  };

  const value = option.DisplayValue || option.LookupValue;

  // Card uses MUI's elevation system (theme.shadows[0-24]) for consistent shadows
  // instead of hardcoded rgba values. This ensures theme compatibility.

  return (
    <ButtonBase onClick={onClick} disableRipple sx={{ borderRadius: 1 }}>
      <Card
        variant="outlined"
        sx={{
          width: 192,
          height: 217,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
          borderRadius: '8px',
          border: '2px solid',
          borderColor: isSelected ? 'secondary.main' : 'transparent',
          borderWidth: isSelected ? '4px' : '1px',
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          boxShadow: isSelected
            ? '0px 1px 18px 0px rgba(0, 0, 0, 0.12), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 3px 5px -1px rgba(0, 0, 0, 0.20)'
            : 'none',
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer',
          '&:hover': {
            borderColor: isSelected ? 'secondary.main' : 'grey.300',
            boxShadow: isSelected
              ? '0px 1px 18px 0px rgba(0, 0, 0, 0.12), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 3px 5px -1px rgba(0, 0, 0, 0.20)'
              : '0px 2px 4px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {/* Icon Container */}
        <Box
          sx={{
            width: { xs: 80, sm: 96 },
            height: { xs: 100, sm: 129 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: { xs: 1, sm: 2 },
            aspectRatio: '32/43',
            // Apply grayscale filter when not selected
            filter: isSelected ? 'none' : 'grayscale(100%)',
            transition: 'filter 0.2s ease-in-out',
          }}
        >
          <img
            src={getSvgSrc(value)}
            alt={value}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Label */}
        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            fontWeight: isSelected ? 600 : 400,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            textAlign: 'center',
            transition: 'font-weight 0.2s ease-in-out',
          }}
        >
          {value}
        </Typography>
      </Card>
    </ButtonBase>
  );
};

export default PropertyTypeCard;
